import React, { useEffect, useMemo, useState } from 'react'
import { ArrowsRightLeftIcon } from '@heroicons/react/24/outline'
import type { WeldTelemetrySampleRow } from '../../utils/weldTelemetryClient'
import {
  defaultExtraSegmentLabel,
  eventOptionsWithSegmentLabels,
  sampleIdsInEventRange
} from '../../utils/passAnalyticsMoveSamples'
import {
  formatSegmentEventRangeLabel,
  resolveSegmentLineStyle,
  splitSamplesByDirectionPass,
  type DirectionPassSegment
} from '../../utils/passAnalyticsGraphAxis'
import AlxModalShell from './weldUpload/AlxModalShell'
import Button from '../ui/Button'
import SingleSelect from '../ui/SingleSelect'

export type MoveSelectionMode = 'segment' | 'custom'

export interface MoveToPassDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: (input: { targetPassLabel: string; sampleIds: number[] }) => void
  sourcePassLabel: string
  /** WPS pass labels from Pass Details (Rename Pass style). */
  wpsPassLabels: string[]
  samples: WeldTelemetrySampleRow[]
  sourceFilenames?: string[]
  busy?: boolean
}

/**
 * Pass Analytics "Move to pass" dialog — Data360 shell + SingleSelect dropdowns.
 * Pick a graph segment (default: last / unknown series) or a custom event-ID range.
 */
export default function MoveToPassDialog({
  open,
  onClose,
  onConfirm,
  sourcePassLabel,
  wpsPassLabels,
  samples,
  sourceFilenames,
  busy = false
}: MoveToPassDialogProps) {
  const destinationOptions = useMemo(
    () => wpsPassLabels.filter((label) => label !== sourcePassLabel),
    [wpsPassLabels, sourcePassLabel]
  )

  const segments = useMemo(
    () => splitSamplesByDirectionPass(samples, sourceFilenames),
    [samples, sourceFilenames]
  )

  const eventOptions = useMemo(
    () => eventOptionsWithSegmentLabels(samples, sourceFilenames),
    [samples, sourceFilenames]
  )
  const eventOptionValues = useMemo(() => eventOptions.map((o) => o.value), [eventOptions])
  const eventLabelByValue = useMemo(() => {
    const map = new Map<string, string>()
    for (const o of eventOptions) map.set(o.value, o.label)
    return map
  }, [eventOptions])

  const [selectionMode, setSelectionMode] = useState<MoveSelectionMode>('segment')
  const [selectedSegmentLabel, setSelectedSegmentLabel] = useState('')
  const [eventFrom, setEventFrom] = useState('')
  const [eventTo, setEventTo] = useState('')
  const [targetPassLabel, setTargetPassLabel] = useState('')

  useEffect(() => {
    if (!open) return
    setSelectionMode('segment')
    setSelectedSegmentLabel(defaultExtraSegmentLabel(segments))
    setEventFrom('')
    setEventTo('')
    setTargetPassLabel('')
  }, [open, segments])

  const selectedSegment: DirectionPassSegment | null = useMemo(() => {
    if (!selectedSegmentLabel) return null
    return segments.find((s) => s.legendLabel === selectedSegmentLabel) ?? null
  }, [segments, selectedSegmentLabel])

  const rangeSampleIds = useMemo(() => {
    if (selectionMode === 'segment') {
      return selectedSegment ? selectedSegment.samples.map((s) => s.sampleId) : []
    }
    const from = Number(eventFrom)
    const to = Number(eventTo)
    if (!Number.isInteger(from) || !Number.isInteger(to)) return []
    return sampleIdsInEventRange(samples, from, to)
  }, [selectionMode, selectedSegment, eventFrom, eventTo, samples])

  const canSubmit =
    !busy &&
    targetPassLabel !== '' &&
    rangeSampleIds.length > 0 &&
    destinationOptions.includes(targetPassLabel)

  const subtitleParts = [`From ${sourcePassLabel}`]
  if (rangeSampleIds.length > 0) {
    subtitleParts.push(
      `${rangeSampleIds.length.toLocaleString()} sample${rangeSampleIds.length === 1 ? '' : 's'}`
    )
  }

  const footer = (
    <div className="flex items-center justify-end gap-2">
      <Button
        type="button"
        variant="secondary"
        data-testid="pa-move-to-pass-cancel"
        disabled={busy}
        onClick={onClose}
      >
        Cancel
      </Button>
      <Button
        type="button"
        data-testid="pa-move-to-pass-confirm"
        disabled={!canSubmit}
        onClick={() => {
          if (!canSubmit) return
          onConfirm({ targetPassLabel, sampleIds: rangeSampleIds })
        }}
      >
        {busy ? 'Moving…' : 'Move samples'}
      </Button>
    </div>
  )

  return (
    <AlxModalShell
      open={open}
      onClose={onClose}
      title="Move to pass"
      subtitle={subtitleParts.join(' · ')}
      width="md"
      testId="pa-move-to-pass-dialog"
      titleId="pa-move-to-pass-title"
      closeDisabled={busy}
      footer={footer}
      icon={<ArrowsRightLeftIcon className="h-5 w-5" aria-hidden />}
    >
      <fieldset className="flex flex-col gap-3" data-testid="pa-move-to-pass-selection">
        <legend className="text-[11px] font-semibold uppercase tracking-wide text-heritage-grey mb-2">
          Select samples from this pass
        </legend>

        <label className="flex items-start gap-2 text-sm text-text-grey">
          <input
            type="radio"
            name="pa-move-selection"
            data-testid="pa-move-to-pass-mode-segment"
            className="mt-1"
            checked={selectionMode === 'segment'}
            onChange={() => setSelectionMode('segment')}
            disabled={busy || segments.length === 0}
          />
          <span className="font-medium">Pass segment</span>
        </label>

        {selectionMode === 'segment' ? (
          <ul
            data-testid="pa-move-to-pass-segment-list"
            className="ml-6 divide-y divide-[#E2E5EA] overflow-hidden rounded-lg border border-[#E2E5EA] bg-page-bg"
          >
            {segments.map((segment) => {
              const style = resolveSegmentLineStyle(segment.legendLabel)
              const eventRange = formatSegmentEventRangeLabel(segment)
              const checked = selectedSegmentLabel === segment.legendLabel
              return (
                <li key={segment.legendLabel}>
                  <label
                    data-testid={`pa-move-to-pass-segment-${segment.legendLabel}`}
                    className={`flex cursor-pointer items-center gap-2 px-3 py-2.5 text-xs ${
                      checked ? 'bg-white' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name="pa-move-segment"
                      className="flex-shrink-0"
                      checked={checked}
                      onChange={() => setSelectedSegmentLabel(segment.legendLabel)}
                      disabled={busy}
                    />
                    <span
                      aria-hidden
                      className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                      style={{ backgroundColor: style.borderColor }}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium text-text-grey">{segment.legendLabel}</span>
                      <span className="block text-heritage-grey">
                        {eventRange} · {segment.samples.length.toLocaleString()} samples
                      </span>
                    </span>
                  </label>
                </li>
              )
            })}
          </ul>
        ) : null}

        <label className="flex items-start gap-2 text-sm text-text-grey">
          <input
            type="radio"
            name="pa-move-selection"
            data-testid="pa-move-to-pass-mode-custom"
            className="mt-1"
            checked={selectionMode === 'custom'}
            onChange={() => setSelectionMode('custom')}
            disabled={busy}
          />
          <span className="font-medium">Custom event range</span>
        </label>

        {selectionMode === 'custom' ? (
          <div className="ml-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div data-testid="pa-move-to-pass-event-from" className="flex flex-col gap-1 text-xs">
              <span className="font-semibold text-heritage-grey">Event ID from</span>
              <SingleSelect
                value={eventFrom}
                options={eventOptionValues}
                onChange={setEventFrom}
                placeholder="Select…"
                searchPlaceholder="Search event…"
                getOptionLabel={(v) => eventLabelByValue.get(v) ?? v}
                dropdownPortal
                interactionLocked={busy || eventOptionValues.length === 0}
                className="w-full"
              />
            </div>
            <div data-testid="pa-move-to-pass-event-to" className="flex flex-col gap-1 text-xs">
              <span className="font-semibold text-heritage-grey">Event ID to</span>
              <SingleSelect
                value={eventTo}
                options={eventOptionValues}
                onChange={setEventTo}
                placeholder="Select…"
                searchPlaceholder="Search event…"
                getOptionLabel={(v) => eventLabelByValue.get(v) ?? v}
                dropdownPortal
                interactionLocked={busy || eventOptionValues.length === 0}
                className="w-full"
              />
            </div>
            {eventFrom !== '' && eventTo !== '' && rangeSampleIds.length === 0 ? (
              <p
                data-testid="pa-move-to-pass-range-empty"
                className="col-span-full text-xs text-warning-700"
              >
                No samples on this pass fall in that event ID range.
              </p>
            ) : null}
          </div>
        ) : null}
      </fieldset>

      <div className="mt-5 flex flex-col gap-1 text-xs" data-testid="pa-move-to-pass-destination">
        <span className="font-semibold text-heritage-grey">Destination pass</span>
        <SingleSelect
          value={targetPassLabel}
          options={destinationOptions}
          onChange={setTargetPassLabel}
          placeholder="Select a pass…"
          searchPlaceholder="Search pass…"
          dropdownPortal
          interactionLocked={busy || destinationOptions.length === 0}
          className="w-full"
          inputClassName={
            targetPassLabel
              ? undefined
              : 'border-warning-300 bg-warning-50 focus:border-warning-400 focus:ring-warning-200'
          }
        />
        {destinationOptions.length === 0 ? (
          <p data-testid="pa-move-to-pass-dest-empty" className="text-warning-700">
            No other pass labels are available. Open <strong>Pass Details</strong>, add the
            destination pass (e.g. Strip), save, then return here — it will show up in this list.
          </p>
        ) : (
          <p data-testid="pa-move-to-pass-dest-hint" className="text-[11px] text-heritage-grey">
            Destination options come from Pass Details. Add the destination pass (e.g. Strip) there, save, then return here. It will show up in this list.
          </p>
        )}
      </div>
    </AlxModalShell>
  )
}
